import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import React, {useEffect, useState} from 'react';
import fetchWithCSRF from '../../helper/fetch-with-csrf';
import findProductSelection from '../../helper/find-product-selection';
import Url from '../../helper/url';
import addToCart from '../actions/add-to-cart';
import changeItemAsync from '../actions/change-item';
import openIntoCartLayer from '../actions/open-into-cart-layer';
import CheckoutErrorLayer from '../layers/CheckoutErrorLayer.jsx';
import openLayer from '../layers/open-layer';
import Logger from '../logger';
import Messages from '../messages';
import ModelContainer from '../model-container';
import ChangeItemLayerContent from './ChangeItemLayerContent.jsx';
import ChangeItemLandmarks from './changeItemLandmarks';
import findCorrectDigistyleId from '../../helper/find-digistyle-helper.js';
import {BottomSheet, Button, Dialog, Typography} from '@bonprix/design-system';
import Breakpoint from '@core/scripts/helper/breakpoint';
import AvailabilityReminderContent from './AvailabilityReminderContent.jsx';
import submitAvailabilityReminderHandler from '../actions/submit-availability-reminder-handler.js';
import AppAvailabilityReminder from '../actions/app-availability-reminder.js';
import {emailFormatValidator} from '@core/scripts/helper/validation/email-validator';
import {hasError, maxLengthValidator, validatorOf} from '@core/scripts/helper/validation/validators';
import Landmark from '@core/scripts/components/landmark-adapter';

const DESKTOP_DEVICE_BREAKPOINTS = ['medium', 'large', 'xlarge', 'max'];
const CART_LAYER = 'cartLayer';

export default function ChangeItemLayer({layerOrigin, channelId, pageType, fkIdOverride, isLastCheckPage}) {
    const [isItemOptionError, setIsItemOptionError] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [notifiable, setNotifiable] = useState(false);
    const [product, setProduct] = useState(null);
    const [texts, setTexts] = useState(null);
    const [selectedStyle, setSelectedStyle] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemOption, setSelectedItemOption] = useState(null);
    const [cartItemId, setCartItemId] = useState(null);
    const [quantity, setQuantity] = useState(null);
    const [availabilityReminderEmail, setAvailabilityReminderEmail] = useState(ModelContainer.get('sessionEmail'));
    const [item, setItem] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [sizeSelected, setSizeSelected] = useState(false);

    const [newsletterSubscriptionErrors, setNewsletterSubscriptionErrors] = useState(null);
    const [isEmailError, setIsEmailError] = useState(false);
    const [isNewsletter, setIsNewsletter] = useState(false);
    const [facadeError, setFacadeError] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [showSuccessLayer, setShowSuccessLayer] = useState(false);

    const isCart = layerOrigin === CART_LAYER;

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(
                Messages.CHANGE_ITEM_LAYER_DATA,
                ({notifiable, origin, id, quantity, item, itemOptionId}) => {
                    setItem(item);
                    setNotifiable(notifiable);
                    setCartItemId(id);
                    setQuantity(quantity);
                    setAvailabilityReminderEmail(ModelContainer.get('sessionEmail'));
                    if (origin === layerOrigin) {
                        fetchData(item, itemOptionId);
                    }
                },
            ),
            MessageBroker.subscribe(Messages.CART.ITEM_CHANGE_SUCCESS, ({origin}) => {
                if (origin !== layerOrigin) {
                    return;
                }
                setDataLoaded(false);
                setIsItemOptionError(false);
            }),
            MessageBroker.subscribe(
                Messages.LAYER.CHANGE_ITEM_AVAILABILITY_REMINDER,
                ({notifiable, itemOption, item}) => {
                    setNotifiable(notifiable);
                    setSelectedItemOption(itemOption);
                    setSelectedItem(item);
                    setSizeSelected(true);
                    setIsItemOptionError(false);
                },
            ),
        ];
        return () =>
            subscriptions.forEach((subscription) => {
                return MessageBroker.unsubscribe(subscription);
            });
    });

    const fetchData = (item, wishlistItemOptionId) => {
        fetchWithCSRF(
            `${Url.toAbsolute('/ajax/change-item-layer/')}?styleId=${findCorrectDigistyleId(item)}&itemId=${item.itemId}${
                item.itemOptionId ? '&itemOptionId=' + item.itemOptionId : ''
            }${item.shopId ? '&shopId=' + item.shopId : ''}`,
        )
            .then((response) => {
                if (response.status >= 400) {
                    Logger.error({response});
                }
                return response.json();
            })
            .then((data) => {
                const {product, texts} = data;
                const {selectedStyle, selectedItem, selectedItemOption} = findProductSelection(
                    product.styles,
                    item.itemId,
                    item.itemOptionId || wishlistItemOptionId,
                    product.coliItemsCount,
                );

                setProduct(product);
                setTexts(texts);
                setSelectedStyle(selectedStyle);
                setSelectedItem(selectedItem);
                setSelectedItemOption(selectedItemOption);
                setDataLoaded(true);
            })
            .catch((error) => {
                Logger.error('Could not get data for ChangeItemLayer', error);
            });
    };

    const onChangeQuantity = (quantity) => {
        setQuantity(quantity);
        ChangeItemLandmarks.sendOnChangeQuantityClickLandmark();
    };

    const handleFormSubmit = async () => {
        ChangeItemLandmarks.sendOnSubmitClickLandmark();

        setSubmitError(false);

        if (!selectedItemOption || selectedItemOption?.id === 0) {
            return setIsItemOptionError(true);
        }

        setLoading(true);

        try {
            if (isCart) {
                await changeItemAsync(
                    cartItemId,
                    selectedItem.id,
                    selectedItemOption.id,
                    quantity,
                    layerOrigin,
                    isLastCheckPage,
                );

                const oldQuantity = item.quantity;
                const quantityShouldBeIncreased = quantity > oldQuantity;
                const sizeChanged = selectedItemOption.id !== item.itemOptionId;
                const colorChanged = selectedItem.id !== item.itemId;
                const changeType = sizeChanged || colorChanged || quantityShouldBeIncreased ? 'INCREASE' : 'DECREASE';

                const transferItem = {
                    itemId: selectedItem.id,
                    itemOptionId: selectedItemOption.id,
                    itemOptionName: selectedItemOption.name,
                    itemNumber: selectedItem.itemNumber,
                    promotionCode: selectedItem.promotionCode,
                    shopId: item.shopId,
                    masterStyleId: item.masterStyleId,
                    productId: findCorrectDigistyleId(item),
                    singlePriceValue: selectedItem.price,
                };
                ChangeItemLandmarks.sendAddActionLandmark(transferItem, oldQuantity, quantity, changeType);
            } else {
                const cartSummary = await addToCart(
                    findCorrectDigistyleId(item),
                    item.shopId,
                    selectedItem?.id,
                    selectedItem?.itemNumber,
                    selectedItemOption?.id,
                    selectedItemOption?.name,
                    selectedItem?.promotionCode,
                    null,
                    layerOrigin,
                    selectedItemOption?.price,
                    1,
                    '',
                    product.masterstyleId,
                    null,
                    null,
                    null,
                );

                if (cartSummary) {
                    closeLayerAndOpenIntoCartLayer();
                } else {
                    setSubmitError(true);
                }
            }
        } catch (error) {
            openLayer(CheckoutErrorLayer, {texts});
            Logger.error(error, new Error());
        }

        setLoading(false);
    };

    const onTextInput = (value) => {
        setAvailabilityReminderEmail(value);
        setIsEmailError(false);
    };

    const onCheckboxChange = () => setIsNewsletter(!isNewsletter);

    const showAvailabilityReminderSuccess = (data, email) => {
        const {success, newsletterSubscriptionResponse, supportsAppAvailabilityReminder} = data;
        // The app only asks for push permission when it is missing, so its prompt is no confirmation:
        // the success view is shown in the app too. The app is only told about a saved reminder.
        if (supportsAppAvailabilityReminder && success) {
            AppAvailabilityReminder.notify(email);
        }
        setShowSuccessLayer(success);
        setAvailabilityReminderEmail(email);
        setNewsletterSubscriptionErrors(newsletterSubscriptionResponse?.errors);
    };

    const validateEmailFormat = emailFormatValidator();
    const emailValidator = validatorOf({format: validateEmailFormat, maxLength: maxLengthValidator(75)});

    const successHandler = (data, email) => {
        setFacadeError(!data.success);
        setSubmitting(false);
        showAvailabilityReminderSuccess(data, email);
    };

    const submitAvailabilityReminder = () => {
        setFacadeError(false);

        if (!selectedItemOption || selectedItemOption?.id === 0) {
            return setIsItemOptionError(true);
        }

        const validationResult = emailValidator.validate(availabilityReminderEmail);
        const hasEmailError = hasError(validationResult);
        setIsEmailError(hasEmailError);

        if (hasEmailError) {
            return;
        }

        setSubmitting(true);

        try {
            sendAvailabilityReminderLandmark();

            return submitAvailabilityReminderHandler(
                availabilityReminderEmail,
                isNewsletter,
                product.styleId ? product.styleId : product.id,
                selectedItem.id,
                selectedItemOption.id,
                selectedItemOption.price,
                successHandler,
            );
        } catch (error) {
            Logger.error('Could not submit availability reminder', error);
            setSubmitting(false);
        }
    };

    const sendAvailabilityReminderLandmark = () => {
        const {channelId, shopId, masterStyleId, styleId} = product;
        const landmarkValue =
            `adp\\availability_mail\\send\\${selectedItem?.itemNumber}\\\\${channelId}\\${shopId}` +
            `\\${masterStyleId}\\${styleId || ''}\\${selectedItemOption?.id || selectedItem?.id}`;
        Landmark.sendClickLandmark(landmarkValue);
    };

    const onClose = () => {
        setDataLoaded(false);
        setIsItemOptionError(false);
        setAvailabilityReminderEmail('');
        setIsEmailError(false);
        setFacadeError(false);
        setSubmitting(false);
        setLoading(false);
        setShowSuccessLayer(false);
        // Reset the size selection so a reopened layer starts clean. Without this, `sizeSelected` stays
        // true across open/close cycles; on the next open of a size-choice article `selectedItemOption`
        // is null again, which made AvailabilityReminderContent pass activeSlideIndex=-1 into the size
        // MiniSlider and crash the whole wishlist (children[-1].offsetWidth).
        setSizeSelected(false);
        setSelectedItemOption(null);
    };

    const closeLayerAndOpenIntoCartLayer = () => {
        setDataLoaded(false);
        setIsItemOptionError(false);

        openIntoCartLayer(selectedItemOption?.id, item.shopId, null, null, fkIdOverride, null, layerOrigin, null);
    };

    if (!dataLoaded) {
        return null;
    }

    const availabilityType = selectedItemOption?.availabilityType;
    const currentNotifiable = notifiable || availabilityType === 'temp';

    const hasSizeChoice =
        !!selectedItem?.itemOptions &&
        !(selectedItem.itemOptions.length === 1 && selectedItem.itemOptions[0].name === '0');
    const needsSizeSelection = hasSizeChoice && (!selectedItemOption || selectedItemOption?.id === 0);

    const showDialog = Breakpoint.keyIsIn(DESKTOP_DEVICE_BREAKPOINTS);

    const reminderHeadline = showSuccessLayer
        ? texts?.bff_wishlist_availability_reminder_success_headline
        : texts?.bff_wishlist_availability_reminder_headline;
    const headline = currentNotifiable ? reminderHeadline : texts?.bff_change_layer_headline;

    const content = (
        <>
            {currentNotifiable ? (
                <AvailabilityReminderContent
                    facadeError={facadeError}
                    showSuccessLayer={showSuccessLayer}
                    currentItem={selectedItem}
                    currentItemOption={selectedItemOption}
                    availabilityReminderEmail={availabilityReminderEmail}
                    texts={texts}
                    isEmailError={isEmailError}
                    newsletterSubscriptionErrors={newsletterSubscriptionErrors}
                    isNewsletter={isNewsletter}
                    onTextInput={onTextInput}
                    onCheckboxChange={onCheckboxChange}
                    sizeSelected={sizeSelected}
                    sizeError={isItemOptionError}
                />
            ) : (
                <ChangeItemLayerContent
                    texts={texts}
                    product={product}
                    item={item}
                    onAddItemToCart={handleFormSubmit}
                    onChangeQuantity={onChangeQuantity}
                    selectedStyle={selectedStyle}
                    selectedItem={selectedItem}
                    selectedItemOption={selectedItemOption}
                    notifiable={notifiable}
                    layerOrigin={layerOrigin}
                    channelId={channelId}
                    dataLoaded={dataLoaded}
                    sizeError={isItemOptionError}
                    quantity={quantity}
                    loading={loading}
                    submitError={submitError}
                    setSelectedItem={setSelectedItem}
                    setSelectedItemOption={setSelectedItemOption}
                    setIsItemOptionError={setIsItemOptionError}
                    setSelectedStyle={setSelectedStyle}
                    setNotifiable={setNotifiable}
                />
            )}
        </>
    );

    const changeButton = showDialog ? (
        <Dialog.PrimaryButton
            onClick={handleFormSubmit}
            disabled={availabilityType === 'soldout'}
            loading={loading}
            size="large"
            fullWidth
        >
            {texts?.cot_confirm_change}
        </Dialog.PrimaryButton>
    ) : (
        <BottomSheet.PrimaryButton
            onClick={handleFormSubmit}
            disabled={availabilityType === 'soldout'}
            loading={loading}
            size="large"
            fullWidth
        >
            {texts?.cot_confirm_change}
        </BottomSheet.PrimaryButton>
    );

    const addToCartButton = showDialog ? (
        <Dialog.PrimaryButton
            icon="cart"
            onClick={handleFormSubmit}
            disabled={availabilityType === 'soldout'}
            loading={loading}
            fullWidth
        >
            {texts?.ada12_add_to_cart}
        </Dialog.PrimaryButton>
    ) : (
        <BottomSheet.PrimaryButton
            icon="cart"
            onClick={handleFormSubmit}
            disabled={availabilityType === 'soldout'}
            loading={loading}
            fullWidth
        >
            {texts?.ada12_add_to_cart}
        </BottomSheet.PrimaryButton>
    );

    const submitButton = layerOrigin === 'cartLayer' ? changeButton : addToCartButton;

    const closeButton = showDialog ? (
        <Dialog.SecondaryButton variant="secondary" onClick={onClose} size="large" fullWidth>
            {texts?.close_layer}
        </Dialog.SecondaryButton>
    ) : (
        <BottomSheet.SecondaryButton variant="secondary" onClick={onClose} size="large" fullWidth>
            {texts?.close_layer}
        </BottomSheet.SecondaryButton>
    );

    const notifyButton = showDialog ? (
        <Dialog.PrimaryButton
            onClick={submitAvailabilityReminder}
            loading={submitting}
            disabled={needsSizeSelection}
            size="large"
            fullWidth
        >
            {texts?.bff_wishlist_availability_reminder_button}
        </Dialog.PrimaryButton>
    ) : (
        // Deliberately a plain Button, NOT BottomSheet.PrimaryButton: the sheet's PrimaryButton
        // force-calls the sheet's onClose after every click, which closes the layer before the async
        // submit resolves (the success view never shows on the first click, only after reopening) and
        // even when email validation fails (the error never shows). Closing stays owned by the
        // component: validation failure keeps the sheet open, and on success the success view with its
        // "Weitershoppen" button is shown instead.
        <Button
            variant="primary"
            onClick={submitAvailabilityReminder}
            loading={submitting}
            disabled={needsSizeSelection}
            size="large"
        >
            {texts?.bff_wishlist_availability_reminder_button}
        </Button>
    );

    const shoppingButton = showDialog ? (
        <Dialog.PrimaryButton onClick={onClose} size="large" fullWidth>
            {texts?.ada_go_on_shopping_button}
        </Dialog.PrimaryButton>
    ) : (
        <BottomSheet.PrimaryButton onClick={onClose} size="large" fullWidth>
            {texts?.ada_go_on_shopping_button}
        </BottomSheet.PrimaryButton>
    );

    return (
        <>
            {/* OPENS IN BOTTOM SHEET FOR MOBILE */}

            <BottomSheet isOpen={dataLoaded} onClose={onClose}>
                <BottomSheet.Content>
                    <div className="availability-reminder__layer">
                        <div className="availability-reminder__headline">
                            <Typography variant="bonprix-h4">{headline}</Typography>
                        </div>
                        {content}
                    </div>
                </BottomSheet.Content>
                <BottomSheet.ButtonSection>
                    {!currentNotifiable && closeButton}
                    {!currentNotifiable && submitButton}
                    {currentNotifiable && !showSuccessLayer && closeButton}
                    {currentNotifiable && !showSuccessLayer && notifyButton}
                    {currentNotifiable && showSuccessLayer && shoppingButton}
                </BottomSheet.ButtonSection>
            </BottomSheet>

            {/* OPENS IN DIALOG FOR DESKTOP */}
            {showDialog && (
                <Dialog zIndex={1000} isOpen={dataLoaded} onClose={onClose}>
                    <Dialog.Title>{headline}</Dialog.Title>
                    <Dialog.Content>{content}</Dialog.Content>
                    <Dialog.ButtonSection>
                        {!currentNotifiable && closeButton}
                        {!currentNotifiable && submitButton}
                        {currentNotifiable && !showSuccessLayer && closeButton}
                        {currentNotifiable && !showSuccessLayer && notifyButton}
                        {currentNotifiable && showSuccessLayer && shoppingButton}
                    </Dialog.ButtonSection>
                </Dialog>
            )}
        </>
    );
}
